import { useState } from "react";
import { Button, Modal, message } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitPublishDiffDto } from "../../../../domain/DataKitDto";
import "./DataKitPublishBar.scss";

interface Props {
  kit: DataKitDto;
  diff: DataKitPublishDiffDto;
  canWrite: boolean;
  onDone: () => void;
}

export function DataKitPublishBar({ kit, diff, canWrite, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (diff.totalChanges === 0) return null;

  // Items and groups, not fields - one item with three edits is one change waiting. Anything off
  // every design isn't counted at all: under the staging rule it applied when it was saved, so a
  // kit no location uses never shows this bar, and the "not assigned yet" variant it used to carry
  // has nothing left to describe.
  const itemCount = diff.itemCount ?? 0;
  const groupCount = diff.groupCount ?? 0;
  const designCount = diff.designCount ?? 0;
  const lead = waitingSentence(itemCount, groupCount);
  const follow =
    designCount > 0
      ? `${designCount} design${designCount === 1 ? "" : "s"} still show${designCount === 1 ? "s" : ""} the last published version.`
      : "Designs still show the last published version.";

  const publish = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.publishKit(kit.id)
      .then((res) => {
        // The banner's own numbers, not r.itemsPublished: publish copies every row in the kit, so
        // that count is the kit's size, and "Published 389 items" after changing one is noise.
        message.success(
          designCount > 0
            ? `Published to ${designCount} design${designCount === 1 ? "" : "s"}`
            : "Published"
        );
        onDone();
      })
      .catch(() => message.error("Could not publish this library"))
      .finally(() => setBusy(false));
  };

  const discard = () => {
    if (!kit.id) return;
    setBusy(true);
    DataKitApi.discardKit(kit.id)
      .then(() => {
        setConfirmDiscard(false);
        onDone();
      })
      .catch(() => message.error("Could not discard these changes"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="data-kit-publish-bar">
      <WarningOutlined className="data-kit-publish-bar-icon" />
      <p className="data-kit-publish-bar-copy">
        <strong>{lead}</strong> <span>{follow}</span>
      </p>
      {canWrite && (
        <>
          <Button
            type="primary"
            className="data-kit-publish-bar-btn"
            onClick={publish}
            loading={busy}
          >
            Publish to designs
          </Button>
          <button
            type="button"
            className="data-kit-publish-bar-discard"
            onClick={() => setConfirmDiscard(true)}
            disabled={busy}
          >
            Discard
          </button>
        </>
      )}

      <Modal
        className={"clean one"}
        open={confirmDiscard}
        onCancel={() => setConfirmDiscard(false)}
      >
        <header>
          <h2>Discard unpublished changes?</h2>
        </header>
        <p className="data-kit-discard-copy">
          Every change waiting to publish goes back to its published value. Items and groups no design
          shows aren&apos;t affected &mdash; their changes already took effect. Item availability isn&apos;t
          affected either; it never waits for a publish.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmDiscard(false)}>
            Cancel
          </Button>
          <Button size="large" className="primary" loading={busy} onClick={discard}>
            Discard
          </Button>
        </footer>
      </Modal>
    </div>
  );
}

/**
 * "[N] items and [N] groups have changes waiting to publish." Drops whichever half is zero, and
 * reads "1 item has" / "1 group has" for one - the AC's exact grammar.
 */
function waitingSentence(items: number, groups: number): string {
  const parts: string[] = [];
  if (items > 0) parts.push(`${items} item${items === 1 ? "" : "s"}`);
  if (groups > 0) parts.push(`${groups} group${groups === 1 ? "" : "s"}`);
  const plural = items + groups !== 1 || parts.length > 1;
  return `${parts.join(" and ")} ${plural ? "have" : "has"} changes waiting to publish.`;
}
